import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';
import { Icon } from './icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 disabled:bg-brand-200',
  secondary: 'bg-surface text-ink border border-border hover:bg-canvas disabled:text-ink-muted',
  ghost: 'text-ink-muted hover:bg-canvas hover:text-ink',
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-200',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  icon?: string;
  /** Shows a busy state and blocks double submits. */
  loading?: boolean;
  /** Icon-only square button. */
  square?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  square = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed',
        size === 'sm' ? 'h-8 text-sm' : 'h-10 text-sm',
        square ? (size === 'sm' ? 'w-8' : 'w-10') : size === 'sm' ? 'px-3' : 'px-4',
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        icon && <Icon name={icon} />
      )}
      {children}
    </button>
  );
}

/** Square icon-only button; `label` is required for accessibility and tests. */
export function IconButton({
  icon,
  label,
  variant = 'ghost',
  ...rest
}: Omit<ButtonProps, 'children' | 'icon'> & { icon: string; label: string }) {
  return (
    <Button variant={variant} size="sm" square aria-label={label} title={label} {...rest}>
      <Icon name={icon} />
    </Button>
  );
}

/**
 * A small set of mutually exclusive choices (language, Roadmap view / data / unit) as
 * pressed/unpressed buttons; `showLabel` also prints the label in front of them.
 */
export function ChoiceButtons<V extends string>({
  label,
  value,
  options,
  onChange,
  showLabel = false,
}: {
  label: string;
  value: V;
  options: readonly { value: V; label: string }[];
  onChange: (value: V) => void;
  showLabel?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      {showLabel && (
        <span aria-hidden className="text-sm font-medium text-ink">
          {label}
        </span>
      )}
      <div role="group" aria-label={label} className="flex gap-1">
        {options.map((o) => (
          <Button
            key={o.value}
            variant="ghost"
            size="sm"
            aria-pressed={o.value === value}
            className={
              o.value === value ? 'bg-brand-50 font-semibold text-brand-700 hover:bg-brand-50' : undefined
            }
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
